import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuid } from 'uuid';
import {
    fetchSubscriptionPageRules,
    fetchSuggestedPageRulesHandledList,
    filterOutPages,
    removeSelectedPageRules,
    updateSuggestedPageRulesHandledList,
    canCreatePages,
    callPageLoadEventsAgg,
    callAiSuggestedPagesAgg,
    convertSubscriptionPageRules,
    generateSuggestedPageRules,
} from './suggested-pages.utils';

export default {
    namespaced: true,
    state: {
        pageLoadEvents: [],
        subscriptionPageRules: [],
        suggestedPageRulesHandledList: [],
        suggestedPageRules: [],
        suggestedPageRulesFilteredData: [],
        error: null,
    },
    getters: {
        isAiSuggestionsEnabled: (state, getters, rootState, rootGetters) => {
            const subscription = rootGetters['subscription/getActiveSub'];
            const hasSegmentFlag = rootGetters['auth/hasSegmentFlag'];

            return (
                get(subscription, 'featureFlags.enableMlSuggestedCountables', false) &&
                hasSegmentFlag('pagesAutoTagging')
            );
        },
    },
    mutations: {
        setPageLoadEvents (state, { pageLoadEvents }) {
            state.pageLoadEvents = pageLoadEvents;
        },
        setSubscriptionPageRules (state, { subscriptionPageRules }) {
            state.subscriptionPageRules = subscriptionPageRules;
        },
        setSuggestedPageRulesHandledList (state, { suggestedPageRulesHandledList }) {
            state.suggestedPageRulesHandledList = suggestedPageRulesHandledList;
        },
        setSuggestedPageRules (state, { suggestedPageRules }) {
            state.suggestedPageRules = suggestedPageRules;
        },
        setSuggestedPageRulesFilteredData (state, { suggestedPageRulesFilteredData }) {
            state.suggestedPageRulesFilteredData = suggestedPageRulesFilteredData;
        },
        setError (state, { error }) {
            state.error = error;
        },
    },
    actions: {
        async hydrate ({ dispatch }) {
            await dispatch('fetchPageLoadEvents');
            await dispatch('fetchSubscriptionPageRules');
            await dispatch('fetchSuggestedPageRulesHandledList');
            await dispatch('createSuggestedPageRules');
        },
        async fetchPageLoadEvents ({ commit, rootState, dispatch, getters, rootGetters }) {
            commit('setError', { err: null });

            const userPreferences = rootGetters['userSettings/crossUISettingByName']('userPreferences');
            const suggestedPagesTableEnabled = get(userPreferences, 'value.suggestedPagesTable.value', false);
            if (!suggestedPagesTableEnabled) {
                commit('setPageLoadEvents', { pageLoadEvents: [] });

                return [];
            }
            const allApps = get(rootState, 'apps.map', {});
            const apps = canCreatePages(allApps);
            if (isEmpty(apps)) {
                commit('setPageLoadEvents', { pageLoadEvents: [] });

                return [];
            }
            const appId = Object.entries(apps).reduce((acc, app) => {
                if (app[1].applicationFlags.suggestPageTags) {
                    acc.push(app[0]);
                }

                return acc;
            }, []);
            if (isEmpty(appId)) {
                commit('setPageLoadEvents', { pageLoadEvents: [] });

                return [];
            }
            let sampleDenominator = 1;
            Object.values(apps).map((app) => {
                if (!isNil(app.eventFilePseudoShardCount) && app.eventFilePseudoShardCount !== 0) {
                    sampleDenominator = app.eventFilePseudoShardCount;
                }

                return sampleDenominator;
            });
            const eventFileShards = get(rootGetters['subscription/getActiveSub'], 'eventFileShards', 0);
            sampleDenominator = Math.max(sampleDenominator, eventFileShards);
            const eventRate = get(rootGetters['subscription/getActiveSub'], 'eventRate', 1);
            const MAX_EVENTS_PER_HOUR = 2500000;
            const MAX_HOUR_COUNT = 164;
            const count = Math.min(MAX_HOUR_COUNT, Math.max(Math.floor(MAX_EVENTS_PER_HOUR / eventRate), 1)) * -1;
            const aggParams = {
                appId,
                blacklist: 'apply',
                timeSeries: {
                    first: -1,
                    period: 'hourRange',
                    count,
                },
                sampleDenominator,
                includeAnonymous: true,
            };

            let pageLoadEvents = [];
            try {
                if (getters.isAiSuggestionsEnabled) {
                    pageLoadEvents = await callAiSuggestedPagesAgg(aggParams);
                } else {
                    pageLoadEvents = await callPageLoadEventsAgg(aggParams);
                }
            } catch (error) {
                commit('setError', { error });
            }

            commit('setPageLoadEvents', { pageLoadEvents });
            dispatch('createSuggestedPageRules');

            return pageLoadEvents;
        },
        async createSuggestedPageRules ({ state, commit, rootState, dispatch, getters }) {
            const subId = get(rootState, 'subscription.active.id', []);

            if (isEmpty(subId)) {
                commit('setSuggestedPageRules', { suggestedPageRules: [] });
                commit('setSuggestedPageRulesFilteredData', { suggestedPageRulesFilteredData: [] });

                return [];
            }
            const pageLoadEvents = get(state, 'pageLoadEvents', []);
            if (isEmpty(pageLoadEvents)) {
                commit('setSuggestedPageRules', { suggestedPageRules: [] });
                commit('setSuggestedPageRulesFilteredData', { suggestedPageRulesFilteredData: [] });

                return [];
            }

            const subscription = get(rootState, `subscription.map['${subId}']`, {});
            if (isEmpty(subscription)) {
                commit('setSuggestedPageRules', { suggestedPageRules: [] });
                commit('setSuggestedPageRulesFilteredData', { suggestedPageRulesFilteredData: [] });

                return [];
            }

            const applications = get(rootState, 'apps.map', {});
            if (isEmpty(applications)) {
                commit('setSuggestedPageRules', { suggestedPageRules: [] });
                commit('setSuggestedPageRulesFilteredData', { suggestedPageRulesFilteredData: [] });

                return [];
            }
            const suggestedPageRulesHandledList = get(state, 'suggestedPageRulesHandledList', []);

            const { isAiSuggestionsEnabled } = getters;
            const suggestedPageRules = pageLoadEvents.reduce((acc, event) => {
                const matchedApplication = Object.values(applications).find((app) => app.id === event.appId);
                const displayName = get(matchedApplication, 'displayName', '');

                // if the suggestions are not ai generated - we need to generate the rule, a name and a sampleCount.
                let sampleCount;
                if (isAiSuggestionsEnabled && Array.isArray(event.rules)) {
                    sampleCount = 5;
                    event = {
                        ...event,
                        name: event.name,
                        rule: [...event.rules].sort().join(', '), // we use rules sorted and serialized form to check later against the handled list.
                        isAiSuggestedResults: true,
                    };
                } else {
                    const { rule, name, sampleCount: generatedSampleCount } = generateSuggestedPageRules(event);
                    sampleCount = generatedSampleCount;
                    event = {
                        ...event,
                        rule,
                        name,
                    };
                }

                event = {
                    ...event,
                    id: uuid(),
                    samples: event.samples.slice(0, sampleCount),
                    application: displayName,
                };
                const ruleHandled = suggestedPageRulesHandledList.find(
                    (app) => app.appId === event.appId && app.rule === event.rule,
                );
                if (ruleHandled) return acc;

                acc.push(event);

                return acc;
            }, []);

            commit('setSuggestedPageRules', { suggestedPageRules });
            dispatch('createSuggestedPageRulesFilteredData');

            return suggestedPageRules;
        },
        createSuggestedPageRulesFilteredData ({ commit, rootState, rootGetters }) {
            const appRules = get(rootState, 'pages.suggestedPages.suggestedPageRules', {});
            if (isEmpty(appRules)) {
                commit('setSuggestedPageRulesFilteredData', { suggestedPageRulesFilteredData: [] });

                return [];
            }

            let selectedFilter = rootGetters['globalFilters/nonAggAppId'];
            if (!selectedFilter) {
                selectedFilter = ['*'];
            }

            const filteredPageRules = filterOutPages(selectedFilter, Object.values(appRules), rootState);

            commit('setSuggestedPageRulesFilteredData', { suggestedPageRulesFilteredData: filteredPageRules });

            return filteredPageRules;
        },
        async fetchSubscriptionPageRules ({ commit, rootGetters }) {
            commit('setError', { err: null });
            let subscriptionPageRules = [];

            const userPreferences = rootGetters['userSettings/crossUISettingByName']('userPreferences');
            const suggestedPagesTableEnabled = get(userPreferences, 'value.suggestedPagesTable.value', false);

            if (!suggestedPagesTableEnabled) {
                return subscriptionPageRules;
            }

            try {
                subscriptionPageRules = await fetchSubscriptionPageRules();
            } catch (error) {
                commit('setError', { error });
            }
            subscriptionPageRules = convertSubscriptionPageRules(subscriptionPageRules);
            commit('setSubscriptionPageRules', { subscriptionPageRules });

            return subscriptionPageRules;
        },
        async fetchSuggestedPageRulesHandledList ({ commit }) {
            commit('setError', { err: null });
            let suggestedPageRulesHandledList = [];

            try {
                suggestedPageRulesHandledList = await fetchSuggestedPageRulesHandledList();
            } catch (error) {
                commit('setError', { error });
            }
            commit('setSuggestedPageRulesHandledList', { suggestedPageRulesHandledList });

            return suggestedPageRulesHandledList;
        },
        async updateAndRefreshTable ({ state, commit, dispatch }, { selections, action }) {
            await updateSuggestedPageRulesHandledList({ selections, action });
            dispatch('fetchSuggestedPageRulesHandledList');
            const suggestedPageRulesFilteredData = get(state, 'suggestedPageRulesFilteredData', []);
            const updatedFilteredData = removeSelectedPageRules(selections, suggestedPageRulesFilteredData);
            commit('setSuggestedPageRulesFilteredData', { suggestedPageRulesFilteredData: updatedFilteredData });
        },
    },
};
